import "./index.css";
import "@fontsource/inter";

import { ReactNode, StrictMode } from "react";
import ReactDOM from "react-dom/client";

import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { auth } from "./lib/auth";
import { CgSpinner } from "react-icons/cg";
import { PostHogProvider } from "posthog-js/react";
import * as Sentry from "@sentry/react";
import { ClerkProvider } from "@clerk/clerk-react";

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://9ea1eed700022def953c27b3dd2ada79@o4508960336248832.ingest.us.sentry.io/4508960338345984",
    integrations: [
      Sentry.feedbackIntegration({
        colorScheme: "system",
      }),
    ],
  });
}

const PostHogProviderWrapper = ({ children }: { children: ReactNode }) => {
  if (import.meta.env.DEV) {
    return <>{children}</>;
  }
  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      }}
    >
      {children}
    </PostHogProvider>
  );
};

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Add your Clerk Publishable Key to the .env file");
}

const queryClient = new QueryClient();
const router = createRouter({
  routeTree,
  context: { queryClient, auth: undefined! },
  defaultPreload: "intent",
  defaultPendingMs: 0,
  defaultPendingMinMs: 50,
  defaultErrorComponent: ({ error }) => (
    <div className="flex h-[80vh] w-full flex-col items-center justify-center">
      <p>{error.message}</p>
    </div>
  ),
  defaultPendingComponent: () => (
    <div className="flex h-[80vh] w-full flex-col items-center justify-center">
      <CgSpinner className="h-24 w-24 animate-spin" />
    </div>
  ),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <PostHogProviderWrapper>
        <ClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          afterSignOutUrl="/login"
          appearance={{
            variables: {
              borderRadius: "0.25rem",
            },
          }}
        >
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} context={{ auth }} />
          </QueryClientProvider>
        </ClerkProvider>
      </PostHogProviderWrapper>
    </StrictMode>,
  );
}
